<template>
  <BaseModal
    :open="true"
    class="paste-diet-modal"
    description="Wklejenie jadłospisu ze schowka zastąpi ten jadłospis."
    title="Czy zastąpić bieżący jadłospis?"
    @close="emit('close')"
  >
    <template #icon>
      <FeaturedIcon background-color="color-base-white" name="AlertTriangle" size="lg" />
    </template>

    <PasteDietComparison
      :copied-days-count="copiedDaysCount"
      :copied-meals-count="copiedMealsCount"
      :current-days-count="currentDaysCount"
      :current-meals-count="currentMealsCount"
    />

    <Divider />

    <PasteDietScaleForm
      v-model:calories="desiredCalories"
      v-model:should-scale="shouldScale"
      :error="caloriesError"
      @calories-click="emit('caloriesClick')"
    />

    <template #footer>
      <div class="paste-diet-modal__footer">
        <Button color="gray" variant="text" @click="emit('clear')">Wyczyść schowek</Button>
        <div class="paste-diet-modal__actions">
          <Button color="gray" @click="emit('close')">Anuluj</Button>
          <Button :disabled="!canConfirm" @click="confirm">Wklej</Button>
        </div>
      </div>
    </template>
  </BaseModal>
</template>

<script lang="ts" setup>
  import { computed, ref } from 'vue'

  import { BaseModal, Button, Divider, FeaturedIcon } from '@/components/ui'

  import PasteDietComparison from './components/PasteDietComparison/PasteDietComparison.vue'
  import PasteDietScaleForm from './components/PasteDietScaleForm/PasteDietScaleForm.vue'

  const props = defineProps<{
    copiedCalories: number
    copiedDaysCount: number
    copiedMealsCount: number
    currentCalories: number
    currentDaysCount: number
    currentMealsCount: number
  }>()

  const emit = defineEmits<{
    caloriesClick: []
    clear: []
    close: []
    confirm: [calories: number]
  }>()

  const shouldScale = ref(false)
  const desiredCalories = ref(String(props.currentCalories))
  const desiredCaloriesNumber = computed(() => Number(desiredCalories.value))
  const desiredCaloriesValid = computed(
    () =>
      desiredCalories.value.trim() !== '' &&
      Number.isInteger(desiredCaloriesNumber.value) &&
      desiredCaloriesNumber.value >= 0 &&
      desiredCaloriesNumber.value <= 9999,
  )
  const canConfirm = computed(() => !shouldScale.value || desiredCaloriesValid.value)
  const caloriesError = computed(() =>
    desiredCaloriesValid.value ? undefined : 'Podaj liczbę całkowitą od 0 do 9999 kcal.',
  )

  const confirm = () => {
    if (!canConfirm.value) return
    emit('confirm', shouldScale.value ? desiredCaloriesNumber.value : props.copiedCalories)
  }
</script>

<style lang="scss">
@use '@repo/ui/typography.scss' as typography;

.paste-diet-modal__comparison {
  border-collapse: collapse;
  color: var(--color-text-text-secondary-700);
  margin-bottom: 20px;
  table-layout: fixed;
  width: 100%;
}

.paste-diet-modal__comparison th,
.paste-diet-modal__comparison td {
  @include typography.font-style('text-sm', 'regular');

  padding: 8px 0;
  text-align: center;
}

.paste-diet-modal__comparison thead th {
  font-weight: 500;
  padding-bottom: 16px;
}

.paste-diet-modal__comparison th:first-child {
  text-align: left;
  width: 35%;
}

.paste-diet-modal__comparison td:last-child {
  align-items: center;
  display: flex;
  gap: 8px;
  justify-content: center;
}

.paste-diet-modal__status {
  align-items: center;
  display: inline-flex;
  flex: none;
}

button.paste-diet-modal__status {
  background: transparent;
  border: 0;
  cursor: help;
  padding: 0;
}

button.paste-diet-modal__status:focus-visible {
  border-radius: var(--radius-xs);
  outline: 2px solid var(--color-effects-focus-rings-focus-ring);
  outline-offset: 2px;
}

.paste-diet-modal__status--warning {
  color: var(--color-foreground-fg-warning-primary);
}

.paste-diet-modal__status--success {
  color: var(--color-foreground-fg-success-primary);
}

.paste-diet-modal__scale-form {
  align-items: start;
  display: grid;
  gap: 12px 16px;
  grid-template-columns: 1fr auto;
  padding-top: 8px;
}

.paste-diet-modal__scale-form > span,
.paste-diet-modal__scale-form > label {
  @include typography.font-style('text-sm', 'medium');

  align-self: center;
  color: var(--color-text-text-secondary-700);
}

.paste-diet-modal__scale-form > label span {
  color: var(--color-brand-600);
}

.paste-diet-modal__scale-form .ui-button {
  min-width: 56px;
}

.paste-diet-modal__scale-form .ui-field {
  width: 132px;
}

.paste-diet-modal__footer {
  align-items: center;
  display: flex;
  gap: 12px;
  justify-content: space-between;
  width: 100%;
}

.paste-diet-modal__actions {
  display: flex;
  gap: 12px;
}

.paste-diet-modal .ui-modal-content {
  padding-top: 24px;
}

@media (width <= 540px) {
  .paste-diet-modal__scale-form {
    grid-template-columns: 1fr;
  }

  .paste-diet-modal__scale-form .ui-field {
    width: 100%;
  }

  .paste-diet-modal__footer {
    align-items: stretch;
    flex-direction: column;
  }

  .paste-diet-modal__actions > .ui-button {
    flex: 1;
  }
}
</style>
